<template>
    <div class="min-h-screen bg-gradient-to-br from-[#e3f0fa] to-[#c8e0f4] text-gray-800 font-sans">
        <header class="bg-gradient-to-r from-[#1583d4] to-[#0f5fa0] text-white py-8 px-6 text-center shadow-lg">
            <h1 class="text-4xl font-extrabold mb-2">
                {{ 'Renove seu Anúncio' }}
            </h1>
            <p class="opacity-90 text-lg">
                {{ 'Siga as etapas abaixo para publicar seu anúncio' }}
            </p>
        </header>

        <div class="container mx-auto max-w-6xl py-12 px-6">
            <div class="mb-6 flex justify-start">
                <button @click="goToHome"
                    class="bg-[#f5b400] hover:bg-[#e0a200] text-white font-semibold px-6 py-2.5 rounded-xl shadow-md transition">
                    Voltar para o Site
                </button>
            </div>
            <form @submit.prevent="handleSubmit" class="space-y-10">
                <!-- Etapa 0: Detalhes -->
                <section v-if="step === 0" class="bg-white/95 backdrop-blur-sm rounded-2xl shadow-lg p-8">
                    <h2 class="text-2xl font-semibold text-[#1583d4] mb-6">Detalhes do Anúncio</h2>
                    <div class="grid grid-cols-1 md:grid-cols-1 gap-12">
                        <v-text-field v-model="form.title" label="Título *" placeholder="Ex: Promoção de Consultoria"
                            variant="outlined" :error-messages="validationErrors.title" />
                    </div>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <v-select v-model="form.category_id" label="Categoria *" variant="outlined"
                            :error-messages="validationErrors.category_id" :items="categories" item-title="description"
                            item-value="id" placeholder="Selecione..." @update:model-value="handleCategoryChange" />
                        <!-- Subcategoria (só aparece se a categoria tiver subcategorias) -->
                        <v-select v-if="selectedCategory && selectedCategory.subcategories.length"
                            v-model="form.subcategory" class="animate-fade-in" label="Subcategoria *" variant="outlined"
                            :error-messages="validationErrors.subcategory" :items="selectedCategory.subcategories"
                            placeholder="Selecione uma subcategoria..." />

                    </div>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div class="d-flex align-end">
                            <div class="flex-grow-1 mr-2">
                                <v-select v-model="form.coverage" label="Alcance de Atuação *" variant="outlined"
                                    :items="[
                                        { title: 'Local', value: 'local' },
                                        { title: 'Estadual', value: 'state' },
                                        { title: 'Nacional', value: 'national' },
                                    ]" item-title="title" item-value="value"
                                    :error-messages="validationErrors.coverage" placeholder="Selecione o alcance..."
                                    hide-details="auto" />
                            </div>

                            <v-menu location="top" width="320">
                                <template #activator="{ props }">
                                    <v-icon v-bind="props" size="24" color="primary" class="mb-2 ml-2">
                                        mdi-help-circle
                                    </v-icon>
                                </template>

                                <v-card class="pa-3">
                                    <h4 class="text-subtitle-1 font-weight-bold">O que é o Alcance de Atuação?</h4>
                                    <p class="text-body-2">
                                        O alcance define a **área geográfica onde sua empresa atende o cliente**.
                                        Isso ajuda o usuário a saber se você pode prestar o serviço na região dele:
                                    </p>

                                    <ul class="text-body-2 pl-4">
                                        <li>
                                            <strong>Local:</strong> Você atende ou entrega produtos/serviços apenas na
                                            sua cidade.
                                        </li>
                                        <li>
                                            <strong>Estadual:</strong> Você atende em qualquer cidade dentro do seu
                                            estado.
                                        </li>
                                        <li>
                                            <strong>Nacional:</strong> Você atende ou entrega em qualquer lugar do
                                            Brasil.
                                        </li>
                                    </ul>
                                </v-card>
                            </v-menu>
                        </div>

                        <v-textarea v-model="form.description" class="md:col-span-2" label="Descrição *"
                            placeholder="Descreva o que está anunciando..." variant="outlined"
                            :error-messages="validationErrors.description" rows="4" />
                    </div>
                </section>

                <!-- Etapa 1: Localização -->
                <section v-if="step === 1" class="bg-white rounded-2xl shadow-md p-8">
                    <h2 class="text-2xl font-semibold text-[#1583d4] mb-6">Localização</h2>
                    <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <v-text-field v-model="form.zip_code" @blur="consultaCEP" label="CEP *" v-maska="'#####-###'"
                            :loading="isCepLoading" placeholder="00000-000" variant="outlined"
                            :error-messages="validationErrors.zip_code" />
                        <v-text-field v-model="form.city" label="Cidade *" placeholder="Cidade" variant="outlined"
                            :error-messages="validationErrors.city" />
                        <v-text-field v-model="form.state" maxlength="2" label="Estado *" placeholder="UF"
                            variant="outlined" :error-messages="validationErrors.state" />
                    </div>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                        <v-text-field v-model="form.address" label="Endereço" placeholder="Rua, número..."
                            variant="outlined" />
                        <v-text-field v-model="form.neighborhood" label="Bairro" placeholder="Ex: Centro"
                            variant="outlined" />
                    </div>
                </section>

                <!-- Etapa 2: Imagem -->
                <section v-if="step === 2" class="bg-white rounded-2xl shadow-md p-8">
                    <h2 class="text-2xl font-semibold text-[#1583d4] mb-6">Imagem</h2>
                    <label for="mainImage" class="upload flex flex-col items-center justify-center text-center py-12">
                        <svg xmlns="http://www.w3.org/2000/svg" class="w-10 h-10 text-[#1583d4]" fill="none"
                            viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                        </svg>
                        <p class="mt-3 text-[#1583d4] font-semibold">Clique para selecionar uma imagem principal</p>
                        <p class="text-sm text-gray-500 mt-1">Formatos aceitos: JPG, PNG, WEBP</p>
                    </label>
                    <input id="mainImage" type="file" accept="image/*" class="hidden" @change="handleImageUpload" />

                    <div v-if="form.mainImagePreview" class="mt-6 flex justify-center">
                        <img :src="form.mainImagePreview" alt="Prévia da imagem"
                            class="w-full max-w-md rounded-lg shadow-md border border-gray-300" />
                    </div>
                    <p v-if="validationErrors.mainImage" class="error">{{ validationErrors.mainImage }}</p>
                </section>

                <!-- Etapa 3: Escolha do Plano -->
                <section v-if="step === 3" class="bg-white rounded-2xl shadow-md p-8">
                    <h2 class="text-2xl font-semibold text-[#1583d4] mb-6">Escolha seu Plano</h2>
                    <div v-if="subscriptions && subscriptions.length" class="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div v-for="plan in subscriptions" :key="plan.id" @click="selectPlan(plan)"
                            class="cursor-pointer border-2 rounded-xl p-6 text-center transition transform hover:scale-105"
                            :class="form.subscription_id === plan.id ? 'border-[#1583d4] bg-blue-50 shadow-lg' : 'border-gray-200 bg-white'">
                            <h3 class="text-xl font-bold text-[#1583d4] capitalize">{{ plan.name }}</h3>
                            <p class="text-gray-600 mt-2">R$ {{ Number(plan.amount).toFixed(2).replace('.', ',') }}</p>
                            <p class="text-sm text-gray-400 mt-1">
                                {{ translatePlanType(plan.plan_type) }}
                            </p>
                        </div>
                    </div>
                    <p v-if="validationErrors.subscription_id" class="error mt-4 text-center">
                        {{ validationErrors.subscription_id }}
                    </p>
                </section>

                <!-- Etapa 4: Termos e Publicação -->
                <section v-if="step === 4" class="bg-white rounded-2xl shadow-md p-8">
                    <h2 class="text-2xl font-semibold text-[#1583d4] mb-6">Termos e Publicação</h2>
                    <!-- Resumo do plano -->
                    <div v-if="selectedPlan" class="mb-6 border border-blue-100 rounded-xl p-5 bg-blue-50/70">
                        <h3 class="text-lg font-semibold text-[#1583d4] mb-2">Resumo do Plano Selecionado</h3>
                        <p><strong>Nome:</strong> {{ selectedPlan.name }}</p>
                        <p><strong>Tipo:</strong> {{ translatePlanType(selectedPlan.plan_type) }}</p>
                        <p><strong>Valor:</strong> R$ {{ Number(selectedPlan.amount).toFixed(2).replace('.', ',') }}</p>
                        <h3 class="text-lg font-semibold text-[#1583d4] mb-2">Período de Publicação</h3>
                        <p>
                            Data de Início:
                            <strong>{{ formatDateToDisplay(dateStartsAt) }}</strong>
                        </p>
                        <p>
                            Data de Expiração:
                            <strong>{{ formatDateToDisplay(dateExpiresAt) }}</strong>
                        </p>

                        <div v-if="!isEmployabilityFreePlan"
                            class="mt-4 flex items-center justify-between bg-white border border-blue-100 rounded-lg p-4">
                            <div>
                                <p class="font-semibold text-[#1583d4]">Deseja destacar seu anúncio?</p>
                                <p class="text-sm text-gray-500">
                                    O destaque aumenta a visibilidade do anúncio.
                                    Valor adicional: <strong>R$ {{ Number(selectedPlan.amount_featured ||
                                        0).toFixed(2).replace('.',
                                            ',') }}</strong>
                                </p>
                            </div>
                            <v-switch v-model="form.is_featured" inset color="#1583d4" hide-details />
                        </div>

                        <div v-if="!isEmployabilityFreePlan && !form.is_featured"
                            class="mt-4 flex items-center justify-between bg-white border border-blue-100 rounded-lg p-4">
                            <div>
                                <p class="font-semibold text-[#1583d4]">Deseja impulsionar seu anúncio?</p>
                                <p class="text-sm text-gray-500">
                                    O Impulso posiciona seu anúncio acima dos anúncios comuns nas listas de busca e
                                    categorias,
                                    aumentando sua visibilidade.
                                    Valor adicional: <strong>R$ {{ Number(selectedPlan.amount_impulse ||
                                        0).toFixed(2).replace('.', ',')
                                    }}</strong>
                                </p>
                            </div>
                            <v-switch v-model="form.is_impulse" inset color="#1583d4" hide-details />
                        </div>

                        <!-- Cupom -->
                        <div v-if="!isEmployabilityFreePlan" class="mt-6 flex gap-3 items-center">
                            <v-text-field v-model="form.coupon_code" label="Cupom de Desconto"
                                placeholder="Digite seu cupom" variant="outlined" hide-details class="flex-1" />
                            <button type="button" @click="verificarCupom"
                                class="px-5 py-3 rounded-lg bg-[#1583d4] text-white hover:bg-[#0f5fa0] disabled:opacity-50"
                                :disabled="isCheckingCoupon">
                                {{ isCheckingCoupon ? 'Verificando...' : 'Aplicar cupom' }}
                            </button>
                        </div>

                        <!-- Desconto aplicado -->
                        <div v-if="discountApplied && !isEmployabilityFreePlan"
                            class="mt-4 text-green-700 bg-green-50 border border-green-200 p-3 rounded-lg">
                            <p><strong>Cupom aplicado:</strong> {{ discountData.code }}</p>
                            <p v-if="discountData.type === 'percent'">
                                Desconto: {{ discountData.value }}%
                            </p>
                            <p v-else>
                                Desconto: R$ {{ Number(discountData.value).toFixed(2).replace('.', ',') }}
                            </p>
                        </div>
                        <!-- Valor final atualizado -->
                        <div class="mt-4 bg-blue-100/40 p-3 rounded-lg border border-blue-200 text-gray-800">
                            <p><strong>Valor final:</strong>
                                R$
                                {{ valorFinalComDestaque.toFixed(2).replace('.', ',') }}
                            </p>
                        </div>
                    </div>
                    <!-- Método de Pagamento -->
                    <div v-if="isEmployabilityFreePlan" class="mt-8 p-4 bg-green-50 border border-green-200 rounded-lg">
                        <h3 class="text-lg font-semibold text-green-700">Pagamento Gratuito</h3>
                        <p class="text-sm text-green-600">Este anúncio é classificado como gratuito</p>
                    </div>

                    <div class="mt-8" v-else>
                        <h3 class="text-lg font-semibold text-[#1583d4] mb-3">Método de Pagamento *</h3>
                        <v-select v-model="form.method" :items="[
                            { label: 'PIX', value: 'pix' },
                            { label: 'Cartão', value: 'card' },
                        ]" item-title="label" item-value="value" label="Selecione o método de pagamento"
                            variant="outlined" placeholder="Escolha uma opção"
                            :error-messages="validationErrors.method" />
                    </div>

                    <!-- Termos -->
                    <label class="flex items-start cursor-pointer bg-blue-50 p-4 rounded-lg">
                        <input type="checkbox" v-model="form.terms_accepted" class="mt-1 mr-3 h-5 w-5 text-[#1583d4]">
                        <span class="text-sm text-gray-700">
                            Declaro que as informações são verdadeiras e aceito os termos de divulgação.
                        </span>

                    </label>

                    <p v-if="validationErrors.terms_accepted" class="error mt-2">{{ validationErrors.terms_accepted }}

                    </p>

                </section>

                <!-- Navegação -->
                <div class="flex justify-between pt-8">
                    <button type="button" @click="prevStep" v-if="step > 0"
                        class="px-8 py-3 rounded-xl border bg-white border-gray-300 text-gray-600 hover:bg-gray-100">
                        Voltar
                    </button>

                    <button v-if="step < steps.length - 1" type="button" @click="nextStep"
                        class="px-8 py-3 rounded-xl bg-[#1583d4] text-white hover:bg-[#0f5fa0]">
                        Avançar
                    </button>

                    <button v-else type="submit" :disabled="isSubmitting"
                        class="px-10 py-3 bg-[#1583d4] hover:bg-[#0f5fa0] text-white rounded-xl font-semibold shadow-md disabled:opacity-50">
                        {{ isSubmitting ? 'Enviando...' : 'Publicar Anúncio' }}
                    </button>
                </div>
            </form>
        </div>
    </div>

    <v-dialog v-model="showPixDialog" persistent max-width="600">
        <v-card class="rounded-2xl pa-6 text-center">
            <h2 class="text-lg font-semibold mb-3 text-gray-800">
                Pagamento via Pix
            </h2>

            <div class="flex justify-center mb-4">
                <img v-if="pixData.qr_code_base64" :src="`data:image/png;base64,${pixData.qr_code_base64}`"
                    alt="QR Code Pix" class="w-48 h-48 object-contain border rounded-xl p-2" />
            </div>

            <p class="text-gray-600 mb-4 text-sm">
                Escaneie o QR Code com o app do seu banco<br />
                ou clique abaixo para copiar o código Pix.
            </p>

            <v-textarea v-model="pixData.qr_code" readonly variant="outlined" rows="3" density="comfortable"
                hide-details class="mb-3" />

            <v-btn color="primary" block class="mb-2" @click="copyPixCode">
                Copiar código Pix
            </v-btn>

            <v-btn color="success" block @click="closePixDialog(pixData)">
                Fechar
            </v-btn>
        </v-card>
    </v-dialog>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue"
import api from "@/services/api"
import { useToastStore } from "@/store/toast"
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex"
import dayjs from "dayjs"
import { useLoadingStore } from '@/store/loadingStore'

const route = useRoute();
const router = useRouter();
const loadingStore = useLoadingStore()
const store = useStore();
const toastStore = useToastStore()
const step = ref(0)
const steps = ['Detalhes', 'Localização', 'Imagem', 'Plano', 'Publicação']
const validationErrors = ref({})
const isSubmitting = ref(false)
const categories = ref([])
const subscriptions = ref([])
const allSubscriptions = ref([])
const userData = ref(null)
const isCepLoading = ref(false)
const isCheckingCoupon = ref(false)
const discountData = ref(null)
const advertisement = ref(null);
const advertisementIdOld = ref(null);
const advertisementCodeOld = ref(null);
const advertisementSlugOld = ref(null);
const app_url_api_storage = process.env.VUE_APP_URL_API_STORAGE;

const showPixDialog = ref(false)
const pixData = ref({
    qr_code: "",
    qr_code_base64: "",
    external_reference: "",
    hash: ""
})

function goToHome() {
    window.location.href = '/';
}

const form = ref({
    title: "", category_id: "", description: "", subcategory: "",
    zip_code: "", city: "", state: "", address: "", neighborhood: "",
    mainImage: null, mainImagePreview: null,
    subscription_id: null,
    coupon_code: "",
    terms_accepted: false,
    is_featured: false,
    is_impulse: false,
    amount_impulse: 0,
    method: null,
    coverage: "local",
})

const selectedCategory = computed(() => {
    return categories.value.find(c => c.id === form.value.category_id) || null
})

const dateStartsAt = computed(() => {
    return dayjs().format('YYYY-MM-DD HH:mm:ss');
});

const dateExpiresAt = computed(() => {
    if (!selectedPlan.value) return null;

    const today = dayjs();
    const planType = selectedPlan.value.plan_type;
    let expirationDate;

    if (planType === 'monthly') {
        // Se realmente precisa de 30 dias exatos, use today.add(30, 'day').
        expirationDate = today.add(1, 'month');
    } else if (planType === 'semi-annual') {
        expirationDate = today.add(6, 'month');
    } else if (planType === 'annual') {
        expirationDate = today.add(12, 'month');
    } else {
        return null;
    }

    return expirationDate.format('YYYY-MM-DD HH:mm:ss');
});

function formatDateToDisplay(dateString) {
    if (!dateString) return 'N/A';
    return dayjs(dateString).format('DD/MM/YYYY');
}

const selectedPlan = computed(() => subscriptions.value.find(p => p.id === form.value.subscription_id))
const discountApplied = computed(() => !!discountData.value)
const valorFinal = computed(() => {
    if (!selectedPlan.value) return 0
    const original = Number(selectedPlan.value.amount)
    if (!discountData.value) return original

    if (discountData.value.type === "percent") {
        return original - (original * (Number(discountData.value.value) / 100))
    } else {
        return Math.max(original - Number(discountData.value.value), 0)
    }
})

const valorFinalComDestaque = computed(() => {
    const base = valorFinal.value
    const destaque = form.value.is_featured
        ? Number(selectedPlan.value?.amount_featured || 0)
        : 0
    const impulso = !form.value.is_featured && form.value.is_impulse
        ? Number(selectedPlan.value?.amount_impulse || 0)
        : 0
    return base + destaque + impulso
})

const isEmployabilityFreePlan = computed(() => {
    const isEmployabilityCategory = selectedCategory.value?.is_employability === 1;
    const isEmployabilityPlan = selectedPlan.value?.is_employability_plan === 1;
    const isFreeAmount = valorFinalComDestaque.value === 0;

    // A condição é: Categoria Empregabilidade AND Plano Empregabilidade AND Valor Final Zero
    return isEmployabilityCategory && isEmployabilityPlan && isFreeAmount;
});

watch(isEmployabilityFreePlan, (isFree) => {
    if (isFree) {
        // Se for plano gratuito, define o método como 'free'
        form.value.method = 'free';
        // Limpa o erro de validação (se existir)
        validationErrors.value.method = undefined;
    } else if (form.value.method === 'free') {
        // Se a condição deixar de ser verdadeira (ex: alterou o destaque e o valor subiu),
        // reseta o método para forçar a seleção do usuário.
        form.value.method = null;
    }
}, { immediate: true });

watch(() => form.value.is_featured, (isFeatured) => {
    // Se o Destaque for ativado, o Impulso deve ser desativado
    if (isFeatured) {
        form.value.is_impulse = false;
    }
});

async function verificarCupom() {
    if (!form.value.coupon_code.trim()) {
        toastStore.showToast({ message: 'Digite um cupom para validar.', color: 'warning' })
        return
    }

    isCheckingCoupon.value = true
    try {
        const { data } = await api.get(`/coupons/promo/${form.value.coupon_code}`,
            { params: { code: form.value.coupon_code, promo: true } }
        )
        discountData.value = data.data
        toastStore.showToast({ message: `Cupom ${data.data.code} aplicado com sucesso!`, color: 'success' })
    } catch (error) {
        discountData.value = null
        toastStore.showToast({ message: error?.response?.data?.error || 'Cupom inválido ou expirado.', color: 'error' })
    } finally {
        isCheckingCoupon.value = false
    }
}

async function consultaCEP() {
    const cep = form.value.zip_code?.replace(/\D/g, "")
    if (cep?.length === 8) {
        try {
            isCepLoading.value = true
            const { data } = await api.get(`/cep`, { params: { cep } })
            Object.assign(form.value, {
                city: data.localidade,
                state: data.uf,
                address: data.logradouro,
                neighborhood: data.bairro
            })
        } catch {
            toastStore.showToast({ message: "Erro ao buscar CEP", color: "error" })
        } finally {
            isCepLoading.value = false
        }
    }
}

function handleImageUpload(e) {
    const file = e.target.files[0]
    if (file) {
        form.value.mainImage = file
        form.value.mainImagePreview = URL.createObjectURL(file)
    }
}

async function handleSubmit() {
    if (!validateStep()) return
    isSubmitting.value = true
    try {
        const formData = new FormData()
        Object.entries(form.value).forEach(([key, value]) => {
            if (["mainImage", "mainImagePreview", "amount_featured", "amount_impulse"].includes(key)) return;
            formData.append(key, value ?? "");
        });

        const amount = selectedPlan.value ? Number(selectedPlan.value.amount) : 0
        const destaque = form.value.is_featured ? Number(selectedPlan.value?.amount_featured || 0) : 0
        const impulseAmount = !form.value.is_featured && form.value.is_impulse
            ? Number(selectedPlan.value?.amount_impulse || 0)
            : 0

        const discount = discountData.value
            ? (discountData.value.type === "percent"
                ? (amount * (Number(discountData.value.value) / 100))
                : Number(discountData.value.value))
            : 0

        const totalAmount = amount - discount + destaque + impulseAmount

        formData.append("amount", amount.toFixed(2))
        formData.append("discount", discount.toFixed(2))
        formData.append("amount_featured", destaque.toFixed(2))
        formData.append("amount_impulse", impulseAmount.toFixed(2))
        formData.append("total_amount", totalAmount.toFixed(2))
        formData.append("starts_at", dateStartsAt.value);
        formData.append("expires_at", dateExpiresAt.value);
        formData.append("status", 'in_analysis');

        formData.append("user_id", userData.value.id)
        formData.append("enterprise_id", userData.value.enterprise_id)

        if (discountData.value) {
            formData.append("coupon_id", discountData.value.id)
        }

        if (form.value.mainImage instanceof File) {
            formData.append("mainImage", form.value.mainImage);
        }
        if (advertisementIdOld.value) {
            formData.append("advertisement_id_old", advertisementIdOld.value);
        }
        if (advertisementCodeOld.value) {
            formData.append("advertisement_code_old", advertisementCodeOld.value);
        }
        if (advertisementSlugOld.value) {
            formData.append("advertisement_slug_old", advertisementSlugOld.value);
        }

        const { data } = await api.post("/advertisement-renewal", formData, {
            headers: { "Content-Type": "multipart/form-data" },
        });
        //toastStore.showToast({ message: data.message || "Anúncio criado com sucesso!", color: "success" })

        //await store.dispatch('saveAdvertisementToLocalStorage', data.data.advertisement);

        if (form.value.method === 'card') {
            if (data.data?.payment.payment_url) {
                window.location.href = data.data.payment.payment_url
            }
        } else if (form.value.method === "pix") {
            openPixDialog(data.data.payment)
        } else if (form.value.method === "free") {
            console.log('deu certo');
            window.location.href = `/payment/result?external_reference=${data.data.payment.external_reference}&hash=${data.data.payment.hash}`;
        }
    } catch (error) {
        toastStore.showToast({ message: error?.response?.data?.error || "Erro ao enviar anúncio.", color: "error" })
    } finally {
        isSubmitting.value = false
    }
}

function nextStep() { if (validateStep()) step.value++ }
function prevStep() { step.value-- }

function selectPlan(plan) { form.value.subscription_id = plan.id }
function translatePlanType(type) {
    return { 'monthly': 'Mensal', 'semi-annual': 'Semestral', 'annual': 'Anual' }[type] || type
}

function validateStep() {
    const e = {}
    if (step.value === 0) {
        if (!form.value.title.trim()) e.title = 'Título obrigatório.'
        if (!form.value.category_id) e.category_id = 'Categoria obrigatória.'
        if (!form.value.subcategory) e.subcategory = 'Subcategoria obrigatória.'
        if (!form.value.description.trim()) e.description = 'Descrição obrigatória.'
        if (!form.value.coverage) e.coverage = 'Selecione o alcance do anúncio.'
    } else if (step.value === 1) {
        if (!form.value.zip_code.trim()) e.zip_code = 'CEP obrigatório.'
        if (!form.value.city.trim()) e.city = 'Cidade obrigatória.'
        if (!form.value.state.trim()) e.state = 'Estado obrigatório.'
    } else if (step.value === 2) {
        if (!form.value.mainImage && !form.value.mainImagePreview) e.mainImage = "Imagem principal obrigatória.";
    } else if (step.value === 3) {
        if (!form.value.subscription_id) e.subscription_id = 'Selecione um plano.'
    } else if (step.value === 4) {
        if (!form.value.terms_accepted) e.terms_accepted = 'Você precisa aceitar os termos.'
        if (!form.value.method) e.method = 'Selecione o método de pagamento.'
    }
    validationErrors.value = e
    return Object.keys(e).length === 0
}

async function getCategories() {
    try {
        const { data } = await api.get("/categories", { params: { is_active: 1 } })
        categories.value = data.data.map(cat => ({
            ...cat,
            subcategories: cat.subcategories || []
        }))
    } catch (error) {
        toastStore.showToast({ message: error?.response?.data?.error || "Erro ao carregar categorias.", color: "error" });
    }
}
async function getSubscriptions() {
    try {
        const { data } = await api.get("/subscriptions", { params: { is_active: 1 } })
        subscriptions.value = data.data
        allSubscriptions.value = data.data;
    } catch (error) {
        toastStore.showToast({ message: error?.response?.data?.error || "Erro ao carregar assinaturas.", color: "error" });
    }
}

async function loadAdvertisement() {
    try {
        const { data } = await api.get(`/advertisements/${advertisementIdOld.value}`);
        const adv = data.data;


        let imageUrl = null;
        if (adv.images) {
            try {
                const imageArray = JSON.parse(adv.images);
                if (imageArray.length > 0) {
                    imageUrl = app_url_api_storage + '/images/' + imageArray[0];
                }
            } catch (e) {
                console.error("Erro ao parsear campo 'images':", e);
            }
        }
        Object.assign(form.value, {
            title: adv.title,
            category_id: adv.category_id,
            subcategory: adv.subcategory,
            description: adv.description,
            zip_code: adv.zip_code,
            city: adv.city,
            state: adv.state,
            address: adv.address,
            neighborhood: adv.neighborhood,
            coverage: adv.coverage,
            mainImagePreview: imageUrl || adv.main_image_url,
        });

        const finalImageUrl = form.value.mainImagePreview;
        if (finalImageUrl) {
            try {
                const response = await fetch(finalImageUrl);
                const blob = await response.blob();
                const fileName = finalImageUrl.split('/').pop() || 'image.jpg';
                const file = new File([blob], fileName, { type: blob.type || 'image/jpeg' });
                form.value.mainImage = file;
            } catch (err) {
                console.error("Erro ao converter imagem em File:", err);
            }
        }
    } catch (error) {
        toastStore.showToast({ message: error?.response?.data?.error || "Erro ao carregar anúncio.", color: "error" });
        //router.push("/cadastre-seu-anuncio");
    }
}

async function handleCategoryChange() {
    const cat = selectedCategory.value

    if (!cat) return
    if (cat.is_employability === 1) {

        subscriptions.value = allSubscriptions.value.filter(p => p.is_employability_plan === 1)
    } else {
        console.log(0);
        subscriptions.value = allSubscriptions.value.filter(p => p.is_employability_plan === 0)
    }

    if (!subscriptions.value.some(p => p.id === form.value.subscription_id)) {
        form.value.subscription_id = null;
    }
}

function openPixDialog(data) {
    pixData.value = data
    showPixDialog.value = true
}

function closePixDialog(data) {
    showPixDialog.value = false

    if (data?.external_reference && data?.hash) {
        const redirectUrl = `/payment/result?external_reference=${data.external_reference}&hash=${data.hash}`
        window.location.href = redirectUrl
    }
}

async function copyPixCode() {
    try {
        await navigator.clipboard.writeText(pixData.value.qr_code)
        toastStore.showToast({
            message: "Código Pix copiado com sucesso!",
            color: "success"
        })
    } catch (err) {
        toastStore.showToast({
            message: "Erro ao copiar código Pix.",
            color: "error"
        })
    }
}

onMounted(async () => {
    const storedData = localStorage.getItem("userData")
    if (storedData) {
        const parsed = JSON.parse(storedData);
        userData.value = parsed.user;
        advertisement.value = parsed.user?.advertisement || null;
    }
    const urlParams = new URLSearchParams(window.location.search);
    advertisementIdOld.value = urlParams.get('advertisement_id_old');
    advertisementCodeOld.value = urlParams.get('code');
    advertisementSlugOld.value = urlParams.get('slug');

    loadingStore.show()
    await getCategories()
    await getSubscriptions()
    await loadAdvertisement()
    await handleCategoryChange();
    loadingStore.hide()
})
</script>


<style scoped>
.upload {
    @apply cursor-pointer border-2 border-dashed border-[#1583d4] rounded-xl w-full py-10 text-center hover:bg-blue-50 transition;
}

.error {
    @apply mt-1 text-sm text-red-500;
}
</style>
